import {effect, inject, Injectable, signal} from '@angular/core';
import {BehaviorSubject, filter, fromEvent, Observable, startWith} from 'rxjs';
import {ConfigService} from "../../core/config";
import {Utils} from "../utils/generics.utils";
import {UiUtils} from "../utils/ui.utils";
import {CookiesConsentService} from "../components/cookies-consent/services/cookies-consent.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AppStateService} from "../../core/state/app-state.service";

@Injectable({
  providedIn: 'root'
})
export class UiService {

  private configService = inject(ConfigService);
  private cookiesService = inject(CookiesConsentService);
  private appStateService = inject(AppStateService);
  private hasFooter$ = new BehaviorSubject<boolean>(!this.appStateService.embedded());

  private logoHeight$ = new BehaviorSubject<string | undefined>(this.configService.getWebapp().logoHeight + 'px');

  private logoImage$ = new BehaviorSubject<string>(this.configService.getWebapp().logoImage);

  private logoBackgroundColor$ = new BehaviorSubject<string | undefined>(this.configService.getWebapp().logoBackgroundColor);

  private logoBorderColor$ = new BehaviorSubject<string | undefined>(this.configService.getWebapp()?.logoBorderColor);

  private readonly orientationChanged$ = new BehaviorSubject<'landscape' | 'portrait'>(UiUtils.isLandscape() ? 'landscape' : 'portrait');
  readonly _orientationChanged = this.orientationChanged$.asObservable();
  private lockSupported?: boolean;

  private _screenWidth = signal(0);
  screenWidth = this._screenWidth.asReadonly();
  private _screenHeight = signal(0);
  screenHeight = this._screenHeight.asReadonly();

  constructor() {
    this.initRotationListener();

    effect(() => {
      if (this.appStateService.embedded()) {
        this.cookiesService.acceptCookies();
        this.hideFooter();
      }
    });

    fromEvent(window, 'resize')
      .pipe(
        takeUntilDestroyed(),
        startWith(undefined)
      )
      .subscribe(() => {
        this.onResize();
      });
  }

  onResize() {
    this._screenWidth.set(window.innerWidth);
    this._screenHeight.set(window.innerHeight);
  }

  showFooter() {
    if (!this.appStateService.embedded()) {
      this.hasFooter$.next(true);
    }
  }

  hideFooter() {
    this.hasFooter$.next(false);
  }

  getFooterStatus(): Observable<boolean> {
    return this.hasFooter$.asObservable();
  }

  getLogoHeight(): string | undefined {
    return this.logoHeight$.getValue();
  }

  getLogoImage(): Observable<string> {
    return this.logoImage$.asObservable();
  }

  getLogoBackgroundColor(): string | undefined {
    return this.logoBackgroundColor$.getValue();
  }

  getLogoBorderColor(): string | undefined {
    return this.logoBorderColor$.getValue();
  }

  getStepPositionAsString(position: number, axle: number): string {
    let stringPosition = position.toString();
    // Return string position only if there is 2 axle (ie: 4 tyre position ).
    if (axle === 2) {
      switch (position) {
        case 1:
          stringPosition = 'frontLeft';
          break;
        case 2:
          stringPosition = 'frontRight';
          break;
        case 3:
          stringPosition = 'rearRight';
          break;
        case 4:
          stringPosition = 'rearLeft';
          break;
      }
    } else if (axle === 1) {
      switch (position) {
        case 1:
          stringPosition = 'frontLeft';
          break;
        case 2:
          stringPosition = 'frontRight';
          break;
      }
    }
    return stringPosition;
  }

  getCommunicationKey(): string {
    let key;
    if (this.configService.getWebapp().showMailIdentification && this.configService.getWebapp().showPhoneIdentification) {
      key = '_all'
    } else if (this.configService.getWebapp().showMailIdentification) {
      key = '_mail'
    } else if (this.configService.getWebapp().showPhoneIdentification) {
      key = '_phone'
    } else {
      key = '_no'
    }
    return key;
  }

  getDisplayedCompanyName() {
    const displayName = this.configService.getFeatures().displayCompanyName;
    return Utils.isBlank(displayName) ? this.appStateService.company()?.name : displayName;
  }

  async isLockSupported() {
    let support = this.lockSupported;
    if (support == undefined) {
      support = true;
      try {
        const type = screen.orientation.type;
        // @ts-ignore
        await screen.orientation.lock(type);
        await screen.orientation.unlock();
      } catch (e) {
        if ((e as Error).name !== 'SecurityError') {
          support = false;
        }
      }
      this.lockSupported = support;
    }
    return support;
  }

  private initRotationListener() {
    if (Utils.isOS()) {
      window.onorientationchange = () => {
        this.orientationChanged$.next(UiUtils.isLandscape() ? 'landscape' : 'portrait');
      }
    } else {
      screen.orientation.addEventListener('change', () => {
        this.orientationChanged$.next(UiUtils.isLandscape() ? 'landscape' : 'portrait');
      });
    }
  }

}
